import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'ios' | 'android' | 'other';

const DISMISSED_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 dní

function getPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isDismissed(): boolean {
  const dismissed = localStorage.getItem(DISMISSED_KEY);
  if (!dismissed) return false;
  const dismissedAt = parseInt(dismissed, 10);
  return Date.now() - dismissedAt < DISMISS_DURATION;
}

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState<Platform>('other');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Nezobrazovat, pokud je už nainstalováno nebo dismissed
    if (isStandalone() || isDismissed()) return;

    const detectedPlatform = getPlatform();
    setPlatform(detectedPlatform);

    // iOS - zobrazit instrukce hned (jen v Safari)
    if (detectedPlatform === 'ios') {
      const isSafari = /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent);
      if (isSafari) {
        setShow(true);
      }
      return;
    }

    // Android/desktop - čekat na beforeinstallprompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShow(false);
    } else {
      handleDismiss();
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 p-4 z-50 safe-area-bottom">
      <div className="max-w-md mx-auto">
        {platform === 'ios' ? (
          // iOS instrukce
          <div className="space-y-3">
            <p className="text-center font-medium">
              Přidej si aplikaci na plochu
            </p>
            <div className="flex items-center justify-center gap-2 text-gray-300 text-sm">
              <span>Klepni na</span>
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L12 14M12 2L8 6M12 2L16 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="4" y="10" width="16" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>a pak</span>
              <span className="bg-gray-700 px-2 py-1 rounded text-white font-medium">
                Přidat na plochu
              </span>
            </div>
            <button
              onClick={handleDismiss}
              className="w-full py-2 text-gray-400 hover:text-gray-200 transition-colors text-sm"
            >
              Teď ne
            </button>
          </div>
        ) : (
          // Android/desktop
          <div className="space-y-3">
            <p className="text-center font-medium">
              Nainstaluj si aplikaci pro rychlejší přístup
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleInstall}
                className="flex-1 py-3 bg-beer hover:bg-beer-dark rounded-lg font-medium transition-colors"
              >
                Nainstalovat
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-3 text-gray-400 hover:text-gray-200 transition-colors"
              >
                Teď ne
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
